import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE } from "@/constants/site";

/**
 * SEO helper — sets meta title, description, canonical, Open Graph, Twitter card,
 * and emits structured data (JSON-LD) targeted at LOCAL home service businesses.
 *
 * Always emits: ProfessionalService (Organization) + WebSite schemas.
 * Optional: pass `schemas={[ ...jsonLdObjects ]}` to add page-specific schemas
 * (e.g. Service, BreadcrumbList, FAQPage).
 */
const TARGET_TRADES = [
    "plumbers",
    "HVAC contractors",
    "roofers",
    "electricians",
    "landscapers",
    "painters",
    "pest control",
    "restoration",
    "general contractors",
    "tree service",
    "pool service",
    "solar installers",
    "garage door",
    "window companies",
    "flooring contractors",
    "fencing companies",
    "pressure washing",
    "junk removal",
    "locksmiths",
    "carpet cleaning",
    "handymen",
    "septic service",
    "appliance repair",
    "house cleaning",
];

const KEYWORDS = [
    "home service marketing",
    "marketing for plumbers",
    "marketing for HVAC contractors",
    "marketing for roofers",
    "marketing for electricians",
    "marketing for landscapers",
    "marketing for painters",
    "marketing for pest control",
    "marketing for restoration companies",
    "marketing for general contractors",
    "marketing for handymen",
    "marketing for solar installers",
    "marketing for pressure washing",
    "marketing for junk removal",
    "marketing for cleaning companies",
    "local SEO for home services",
    "local SEO for contractors",
    "Google My Business optimization for trades",
    "GMB optimization",
    "missed call text-back",
    "5-star Google review funnel",
    "review funnel for contractors",
    "unified inbox for home services",
    "business phone for contractors",
    "marketing systems for trades",
    "contractor website design",
    "lead generation for home service businesses",
    "all-in-one marketing platform for trades",
    "GoHighLevel alternative",
].join(", ");

/** Always-on Organization / LocalBusiness style schema */
function buildOrganizationSchema() {
    return {
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        "@id": `${SITE.url}/#organization`,
        name: SITE.name,
        legalName: "Elevara Systems",
        url: SITE.url,
        logo: `${SITE.url}/assets/mascot_pose1.png`,
        image: `${SITE.url}/assets/mascot_pose1.png`,
        description: SITE.description,
        slogan: SITE.tagline,
        priceRange: "$$",
        telephone: "+1-941-807-5580",
        email: "contact@theelevara.com",
        sameAs: [
            "https://www.instagram.com/official.elevara/",
        ],
        areaServed: {
            "@type": "Country",
            name: "United States",
        },
        serviceType: [
            "Marketing Systems for Home Service Businesses",
            "Local SEO",
            "Website Design",
            "Review Management",
            "Marketing Automation",
            "Missed Call Text-Back",
            "Business Phone Services",
            "Unified Inbox",
            "Google Business Profile Optimization",
        ],
        knowsAbout: TARGET_TRADES,
        audience: {
            "@type": "BusinessAudience",
            audienceType:
                "Home service business owners — plumbers, HVAC, roofers, electricians, landscapers, painters, pest control, restoration, and contractors across the United States.",
        },
        makesOffer: {
            "@type": "Offer",
            name: "Foundational Advanced",
            description:
                "One plan. Seven marketing systems for home service businesses. $297/month. No bull*. Cancel anytime.",
            url: "https://grow.theelevara.com",
            priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: "297",
                priceCurrency: "USD",
                unitText: "MONTH",
                billingIncrement: 1,
            },
        },
        contactPoint: {
            "@type": "ContactPoint",
            telephone: "+1-941-807-5580",
            email: "contact@theelevara.com",
            contactType: "sales",
            areaServed: "US",
            availableLanguage: ["English"],
        },
    };
}

/** Site-wide WebSite schema */
function buildWebSiteSchema() {
    return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": `${SITE.url}/#website`,
        url: SITE.url,
        name: SITE.name,
        description: SITE.description,
        publisher: { "@id": `${SITE.url}/#organization` },
        inLanguage: "en-US",
    };
}

export function SEO({
    title,
    description,
    path = "/",
    image = "/assets/mascot_pose1.png",
    type = "website",
    schemas = [],
    noindex = false,
}) {
    const fullTitle = title
        ? `${title} | ${SITE.name}`
        : `${SITE.name} — ${SITE.tagline}`;
    const desc = description || SITE.description;
    const url = `${SITE.url}${path}`;
    const ogImage = `${SITE.url}${image}`;

    const robotsContent = noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1";

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            <meta name="keywords" content={KEYWORDS} />
            <meta name="robots" content={robotsContent} />
            <meta name="theme-color" content="#188bf6" />
            <link rel="canonical" href={url} />

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content={SITE.name} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:alt" content={SITE.name} />
            <meta property="og:locale" content="en_US" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={ogImage} />
            <meta name="twitter:image:alt" content={SITE.name} />

            {/* Always-on structured data */}
            <script type="application/ld+json">
                {JSON.stringify(buildOrganizationSchema())}
            </script>
            <script type="application/ld+json">
                {JSON.stringify(buildWebSiteSchema())}
            </script>

            {/* Page-specific schemas (Service, BreadcrumbList, FAQPage, etc.) */}
            {schemas.map((s, i) => (
                <script key={s["@id"] || s["@type"] || `schema-${i}`} type="application/ld+json">
                    {JSON.stringify(s)}
                </script>
            ))}
        </Helmet>
    );
}

/* ---------- Reusable schema builders ---------- */

/** BreadcrumbList for an array of {name, path} */
export function breadcrumbSchema(items) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: it.name,
            item: `${SITE.url}${it.path}`,
        })),
    };
}

/** Service schema for a specific marketing system */
export function serviceSchema({ name, description, slug }) {
    return {
        "@context": "https://schema.org",
        "@type": "Service",
        name: `${name} — for Home Service Businesses`,
        description,
        url: `${SITE.url}/systems/${slug}`,
        provider: { "@id": `${SITE.url}/#organization` },
        areaServed: { "@type": "Country", name: "United States" },
        audience: {
            "@type": "BusinessAudience",
            audienceType:
                "Home service business owners — plumbers, HVAC, roofers, electricians, landscapers, and contractors.",
        },
        category: "Home Service Marketing",
        offers: {
            "@type": "Offer",
            url: "https://grow.theelevara.com",
            priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: "297",
                priceCurrency: "USD",
                unitText: "MONTH",
            },
        },
    };
}

/** FAQPage schema from an array of {q, a} */
export function faqSchema(faqs) {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: {
                "@type": "Answer",
                text: a,
            },
        })),
    };
}

export default SEO;
